import { computed, getCurrentInstance, ref } from "vue";
import { followObject, getMyFollows, unfollowObject } from "@/api";
import { getCommentViewer } from "./commentUtils";

/** Object type that can be followed in v1; every other type hides the control. */
export const FOLLOWABLE_OBJECT_TYPE = "employee";
export const DEFAULT_CADENCE = "daily";
const PAGE_SIZE = 100;

const currentAccountId = () => JSON.parse(localStorage.getItem("currentAccountID"));

/**
 * Follow state of the signed-in user for one Chatter object (ER-12317).
 * The backend has no per-object follow lookup, so `refresh()` pages through
 * GET /follows?access=me until it finds the row for this object.
 * @param {{ getObjectType: () => string, getObjectId: () => (string|number|null) }} options
 */
export function useChatterFollow({ getObjectType, getObjectId }) {
  const proxy = getCurrentInstance()?.proxy;
  const followId = ref(null);
  const cadence = ref(DEFAULT_CADENCE);
  const loaded = ref(false);
  const pending = ref(false);
  let requestSeq = 0;

  const followable = computed(() => {
    const id = getObjectId();
    return getObjectType() === FOLLOWABLE_OBJECT_TYPE && id != null && id !== "";
  });
  const following = computed(() => followId.value != null);

  const fail = (message, error) => {
    console.error(message, error);
    proxy?.$notifications?.error(message);
  };

  const findFollow = async () => {
    const accountId = currentAccountId();
    const objectId = String(getObjectId());
    let offset = 0;
    let knownCadence = null;
    let hasNext = true;
    while (hasNext) {
      const { data } = await getMyFollows(accountId, { limit: PAGE_SIZE, offset });
      const rows = data?.results || [];
      if (!knownCadence && rows[0]?.cadence) knownCadence = rows[0].cadence;
      const match = rows.find(
        (row) =>
          row.object_type_name === FOLLOWABLE_OBJECT_TYPE && String(row.object_id) === objectId
      );
      if (match) return { match, knownCadence };
      offset += rows.length;
      hasNext = Boolean(data?.next) && rows.length > 0;
    }
    return { match: null, knownCadence };
  };

  const reset = () => {
    requestSeq++;
    followId.value = null;
    cadence.value = DEFAULT_CADENCE;
    loaded.value = false;
    pending.value = false;
  };

  /** Re-reads whether the user follows this object and their cadence. */
  const refresh = async ({ silent = false } = {}) => {
    if (!followable.value) {
      reset();
      return;
    }
    const seq = ++requestSeq;
    try {
      const { match, knownCadence } = await findFollow();
      if (seq !== requestSeq) return;
      followId.value = match?.id ?? null;
      cadence.value = knownCadence || DEFAULT_CADENCE;
      loaded.value = true;
    } catch (error) {
      if (seq !== requestSeq || silent) return;
      fail("Failed to load follow status.", error);
    }
  };

  const follow = async () => {
    if (pending.value || !followable.value || following.value) return false;
    const accessId = getCommentViewer().accessId;
    if (!accessId) {
      fail("Failed to follow.", new Error("No access id for the signed-in user"));
      return false;
    }
    pending.value = true;
    try {
      const { data } = await followObject(currentAccountId(), {
        access: accessId,
        object_type: FOLLOWABLE_OBJECT_TYPE,
        object_id: getObjectId(),
      });
      followId.value = data?.id ?? null;
    } catch (error) {
      fail("Failed to follow.", error);
      return false;
    } finally {
      pending.value = false;
    }
    // Cadence is a per-user preference, so a choice made earlier still applies.
    await refresh({ silent: true });
    return true;
  };

  const unfollow = async () => {
    if (pending.value || !following.value) return false;
    pending.value = true;
    try {
      await unfollowObject(currentAccountId(), followId.value);
      followId.value = null;
      return true;
    } catch (error) {
      fail("Failed to unfollow.", error);
      return false;
    } finally {
      pending.value = false;
    }
  };

  /** Mirrors a cadence the FollowMenu already saved. */
  const setCadence = (value) => {
    cadence.value = value;
  };

  return { followable, following, cadence, loaded, pending, refresh, follow, unfollow, setCadence, reset };
}
